/**
 * The big search box understands a buyer's own words: "3 bed Borrowdale under
 * $200k" becomes beds_min=3, max_price=200000 and a text search for
 * "Borrowdale". No AI and no guessing — each rule is a plain pattern, and
 * anything the rules do not recognise is searched as text, exactly as typed.
 *
 * Returns the URL params the phrase sets. Keys the phrase does not mention are
 * returned as '' so a new search replaces the old one instead of stacking on it.
 */

const money = (num, unit) => {
  const n = Number(String(num).replace(/,/g, ''));
  if (!Number.isFinite(n)) return null;
  const u = String(unit || '').toLowerCase();
  if (u === 'k') return Math.round(n * 1000);
  if (u === 'm') return Math.round(n * 1000000);
  return Math.round(n);
};

const AMOUNT = String.raw`(?:us\s*)?\$?\s*([\d][\d,]*(?:\.\d+)?)\s*(k|m)?\b`;

// Feature words → the feature keys the browse API matches (see backend FEATURE_SYNONYMS).
const FEATURE_WORDS = [
  [/\bboreholes?\b/i, 'borehole'],
  [/\bsolar(?:\s+backup)?\b/i, 'solar'],
  [/\bgenerators?\b/i, 'generator'],
  [/\b(?:swimming\s+)?pool\b/i, 'swimming pool'],
  [/\b(?:walled|durawall)\b/i, 'walled'],
  [/\belectric\s+fence\b/i, 'electric fence'],
  [/\bstaff\s+quarters\b/i, 'staff quarters'],
  [/\bserviced\b/i, 'serviced'],
];

const FILLER = /\b(?:for\s+sale|homes?\s+for\s+sale|with|and|a|an|the|in|at|near|around|price|priced|usd)\b/gi;

export const PARSED_KEYS = ['q', 'type', 'beds_min', 'baths_min', 'min_price', 'max_price', 'features'];

export function parseSearch(input) {
  let s = ` ${String(input || '').trim()} `;
  const out = Object.fromEntries(PARSED_KEYS.map((k) => [k, '']));
  const take = (re, fn) => {
    const m = s.match(re);
    if (!m) return;
    fn(m);
    s = s.replace(m[0], ' ');
  };

  // Price range first ("$150k - $250k", "150000 to 250000"), then one-sided bounds.
  take(new RegExp(`${AMOUNT}\\s*(?:-|–|to)\\s*${AMOUNT}`, 'i'), (m) => {
    const lo = money(m[1], m[2]);
    const hi = money(m[3], m[4]);
    if (lo != null) out.min_price = String(lo);
    if (hi != null) out.max_price = String(hi);
  });
  take(new RegExp(`\\b(?:under|below|less\\s+than|max(?:imum)?|up\\s+to|<)\\s*${AMOUNT}`, 'i'), (m) => {
    const v = money(m[1], m[2]); if (v != null) out.max_price = String(v);
  });
  take(new RegExp(`\\b(?:over|above|more\\s+than|min(?:imum)?|from|at\\s+least|>)\\s*${AMOUNT}`, 'i'), (m) => {
    const v = money(m[1], m[2]); if (v != null) out.min_price = String(v);
  });

  take(/\b(\d{1,2})\s*\+?\s*(?:-\s*)?(?:bed(?:room)?s?|br)\b/i, (m) => { out.beds_min = m[1]; });
  take(/\b(\d{1,2}(?:\.5)?)\s*\+?\s*(?:-\s*)?(?:bath(?:room)?s?)\b/i, (m) => { out.baths_min = m[1]; });

  take(/\b(?:houses?|homes?)\b/i, () => { out.type = 'house'; });
  if (!out.type) take(/\b(?:stands?|plots?|land)\b/i, () => { out.type = 'stand'; });

  const features = [];
  FEATURE_WORDS.forEach(([re, key]) => take(re, () => features.push(key)));
  out.features = features.join(',');

  out.q = s.replace(FILLER, ' ').replace(/[,;]+/g, ' ').replace(/\s+/g, ' ').trim();
  return out;
}

/** The phrase the box should show for the current URL, so a reload reads naturally. */
export function describeSearch(values = {}) {
  const parts = [];
  if (values.beds_min) parts.push(`${values.beds_min} bed`);
  if (values.type === 'house') parts.push('house');
  if (values.type === 'stand') parts.push('stand');
  if (values.q) parts.push(values.q);
  if (values.min_price && values.max_price) parts.push(`$${Number(values.min_price).toLocaleString('en-US')} to $${Number(values.max_price).toLocaleString('en-US')}`);
  else if (values.max_price) parts.push(`under $${Number(values.max_price).toLocaleString('en-US')}`);
  else if (values.min_price) parts.push(`over $${Number(values.min_price).toLocaleString('en-US')}`);
  return parts.join(' ');
}
